import React from 'react';

interface MinimalLazyVideoProps {
  src: string;
  poster?: string;
  className?: string;
  style?: React.CSSProperties;
  postId?: string; // For stable keys
}

/**
 * Minimal LazyVideo with stable patterns
 * - Sets src only once (never clears)
 * - Stable key per post
 * - Single video element
 * - Fade in after loadeddata
 */
export function MinimalLazyVideo({ 
  src, 
  poster, 
  className = '',
  style = {},
  postId
}: MinimalLazyVideoProps) {
  const ref = React.useRef<HTMLVideoElement>(null);
  const armedRef = React.useRef(false); // Prevent repeated src setting
  const ready = React.useRef(false);
  const [cls, setCls] = React.useState("opacity-0");
  const [armed, setArmed] = React.useState(false);

  const armVideo = React.useCallback(() => {
    const el = ref.current;
    if (!el || armedRef.current) return;
    
    el.src = src; // Set once, never clear
    armedRef.current = true;
    setArmed(true);
  }, [src]);

  // Auto-arm when visible (one-shot)
  React.useEffect(() => {
    const el = ref.current;
    if (!el || armedRef.current) return;
    
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !armedRef.current) {
        armVideo();
        io.disconnect(); // One-shot: disconnect after arming
      }
    }, { rootMargin: '200px' });
    
    io.observe(el);
    return () => io.disconnect();
  }, [armVideo]);

  // Listen for loadeddata to show video
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleLoadedData = () => { 
      if (!ready.current) { 
        ready.current = true; 
        setCls("opacity-100"); 
      } 
    };
    el.addEventListener('loadeddata', handleLoadedData);
    
    return () => el.removeEventListener('loadeddata', handleLoadedData);
  }, []);

  // Handle click/tap to arm or play
  const handleVideoClick = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    
    // Arm first if not armed
    if (!armedRef.current) {
      armVideo();
      return;
    }
    
    // Play/pause if already armed
    if (el.paused) {
      el.play().catch(console.warn);
    } else {
      el.pause();
    }
  }, [armVideo]);

  // iOS-specific attributes
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const videoProps = isIOS ? {
    playsInline: true,
    'webkit-playsinline': 'true',
    preload: 'none' as const
  } : {
    preload: 'none' as const
  };

  return (
    <div className="relative">
      {/* Single stable video element */}
      <video
        key={postId ? `video-${postId}` : `video-${src}`}
        ref={ref}
        poster={poster}
        muted
        controls={armed}
        className={`${className} will-change-auto transform translate-z-0 ${cls}`}
        onClick={handleVideoClick}
        onError={(e) => {
          console.warn('Video error:', (e.target as HTMLVideoElement).currentSrc);
        }}
        style={{ 
          width: '100%', 
          background: '#000',
          borderRadius: 12,
          cursor: ready.current ? 'default' : 'pointer',
          ...style 
        }}
        {...videoProps}
      />
      
      {/* Play button overlay - only when not armed */}
      {!armed && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none rounded-xl">
          <button 
            className="bg-white/90 rounded-full p-4 transform translate-z-0 pointer-events-auto"
            onClick={handleVideoClick}
            aria-label="Tap to load video"
          >
            <svg className="w-8 h-8 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}